import { Eye } from "@tamagui/lucide-icons";
import { ServiceRequest, ServiceRequestStatus } from "common";
import { useTranslation } from "react-i18next";
import { YStack, XStack, Text } from "tamagui";
import { OutlinedButton, Table } from "tamagui-config";

import { StatusPill } from "../../components/StatusPill";

export interface ServiceRequestDisplay extends ServiceRequest {
  propertyId?: string;
  systemAlert?: string;
  customerRequest?: string;
  requestClosed?: string;
  requestedDate: string;
  requestedTimeSlot?: string | null;
  deviceRoom?: string;
  customerName: string;
  customerAddress?: string;
  notes?: string;
  hubName?: string;
}

export const ServiceRequestTableRow = ({
  request,
  onViewServiceRequest,
  isFirst,
  isLast,
}: {
  request: ServiceRequestDisplay;
  onViewServiceRequest: (id: string) => void;
  isFirst: boolean;
  isLast: boolean;
}) => {
  const { t } = useTranslation();
  const getStatusBadge = (status: ServiceRequestStatus | string) => {
    switch (status) {
      case ServiceRequestStatus.OPEN:
        return (
          <StatusPill success>{t("serviceRequests.statuses.open")}</StatusPill>
        );
      case ServiceRequestStatus.PENDING:
        return (
          <StatusPill warning>
            {t("serviceRequests.statuses.pending")}
          </StatusPill>
        );
      case "in_progress":
        return (
          <StatusPill backgroundColor="$hgPrimary" borderColor="$hgPrimary">
            <Text color="$color1" fontSize="$3">
              {t("serviceRequests.statuses.inProgress")}
            </Text>
          </StatusPill>
        );
      case ServiceRequestStatus.CANCELED:
        return (
          <StatusPill error>
            {t("serviceRequests.statuses.canceled")}
          </StatusPill>
        );
      case ServiceRequestStatus.RESCHEDULE:
        return (
          <StatusPill info>
            {t("serviceRequests.statuses.reschedule")}
          </StatusPill>
        );
      case ServiceRequestStatus.CLOSED:
        return <StatusPill>{t("serviceRequests.statuses.closed")}</StatusPill>;
      default:
        return <StatusPill>{status}</StatusPill>;
    }
  };

  return (
    <Table.Row rowLocation={isFirst ? "first" : isLast ? "last" : "middle"}>
      <Table.Cell>
        <YStack>
          <Text fontWeight="bold" fontSize="$3">
            {request.customerName}
          </Text>
          <Text color="$gray10" fontSize="$2">
            {request.customerAddress}
          </Text>
        </YStack>
      </Table.Cell>
      <Table.Cell>
        <Text fontSize="$3">
          {t(`serviceRequests.serviceTypes.${request.serviceType}`)}
        </Text>
      </Table.Cell>
      <Table.Cell>{getStatusBadge(request.status)}</Table.Cell>
      <Table.Cell>
        <Text fontSize="$3">{request.systemAlert || "-"}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text fontSize="$3">{request.customerRequest || "-"}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text fontSize="$3">{request.requestClosed || "-"}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text fontSize="$3">{request.deviceRoom}</Text>
      </Table.Cell>
      <Table.Cell>
        <XStack justifyContent="center">
          <OutlinedButton
            rounded
            icon={Eye}
            fontSize="$3"
            onPress={() => onViewServiceRequest(request.id)}
          />
        </XStack>
      </Table.Cell>
    </Table.Row>
  );
};
